// De un nombre de color a un color de pantalla.
//
// POR QUÉ ESTE ARCHIVO NO EXISTÍA HASTA AHORA
//
// En toda la app los colores son NOMBRES: "Azuloscuro", "Borgoña". La
// base los guarda como texto, las reglas de estilo los comparan como
// texto y el filtro los agrupa en familias como texto. Nunca hizo falta
// saber cómo se ven, porque siempre estaba la foto al lado.
//
// El calendario es la primera pantalla donde NO hay foto. Un mes son
// treinta días y noventa prendas; mostrar eso en imágenes pesaría cinco
// veces más que la página de sugerencias entera. Así que el mes se
// dibuja con los colores, y para eso hay que traducirlos.
//
// LO QUE ESTO NO ES
//
// No es el color real de la prenda. Es el color con que se la NOMBRÓ, y
// eso es una etiqueta gruesa: dos camisas distintas marcadas "Azul" se
// dibujan idénticas. Para lo que sirve el calendario —ver que toda la
// semana fue oscura, o que hace un mes que no usás nada de color— la
// etiqueta alcanza. Para elegir qué ponerse, no: para eso está la foto,
// que sigue apareciendo al tocar el día.
//
// COLORES PROPIOS DEL USUARIO
//
// Un usuario puede crear colores (ver supabase/colores.sql). Esos no
// están en la tabla de acá y no pueden estarlo: son suyos, y viven en
// su cuenta.
//
// Desde 2026-08-21 un color propio ELIGE SU MUESTRA al crearse
// (`colores_usuario.hex`), y se registra acá con `registrarPropios`
// cuando la app carga las opciones. Los que se crearon antes de eso no
// tienen hex: siguen dibujándose con una trama gris que se distingue
// de un gris de verdad, con el nombre en el tooltip.
//
// La trama nunca fue un placeholder perezoso: "no sé de qué color es"
// es distinto de "es gris", y un "Fucsia" pintado de un rosa cualquiera
// sería una mentira chiquita repetida treinta veces en un calendario.

// Los 21 de lib/opciones.js. Elegidos para que se distingan entre sí en
// un cuadrito de pocos píxeles, no para ser fieles a un pantone.
const TINTES = {
  Negro: "#1c1c1e",
  Blanco: "#f7f7f5",
  Gris: "#9a9a9e",
  Beige: "#d8c9ac",

  Azul: "#3b6fd4",
  Azulclaro: "#7fb0e8",
  Azuloscuro: "#1f3a6e",
  Celeste: "#a8d8f0",
  Petroleo: "#1f5c66",

  Rojo: "#c8322f",
  Borgoña: "#6e1f2e",

  Verde: "#3e8e52",
  Verdeclaro: "#8fc98a",

  // Acá había una entrada extra, "Marron" sin tilde, duplicando este
  // mismo hex. Venía de la migración de Notion, que dejó el color
  // escrito de dos formas en la lista base.
  //
  // Se sacó el 2026-08-23, después de contar: CERO prendas usaban la
  // versión sin tilde. Y aunque alguna la usara, `tinteDe` compara con
  // `clavePegada()` —que saca los acentos— así que "Marron" encuentra a
  // "Marrón" igual. El alias no hacía falta ni antes.
  Marrón: "#7a5230",
  MarrónOscuro: "#4a3120",

  Naranja: "#e07b39",
  Amarillo: "#e8c547",
  Morado: "#7a4a9e",
  // Minúscula a propósito: así está en la lista base.
  violeta: "#9b6fc4",
  Rosa: "#e39ab8",
};

// Cuando el color no está en la lista. Gris con trama: se lee como
// "no sé de qué color es" y no como "es gris".
const DESCONOCIDO = "desconocido";

/* Los colores propios del usuario, por clave normalizada.
 *
 * Es un registro de módulo y no un parámetro que se pasa de componente
 * en componente. La alternativa era hacer que Calendario, Outfits y
 * cualquier pantalla futura que dibuje un color reciban la lista por
 * props, y eso significa que agregar una pantalla nueva es acordarse de
 * este detalle otra vez — no acordarse se ve como "mi color sale gris",
 * que es indistinguible de un color sin hex.
 *
 * Lo llena `cargarOpciones()`, que ya pide los colores propios. Se
 * vacía al cerrar sesión: son de ESTE usuario, y en la misma pestaña
 * puede entrar otro.
 */
let PROPIOS = new Map();

function registrarPropios(lista) {
  PROPIOS = new Map(
    (lista || [])
      .filter((c) => c?.nombre && c?.hex)
      /* ⚠️  SE CALCULA, NO SE COPIA DE LA FILA, y eso costó pensarlo.
       *
       * La fila trae `colores_usuario.clave`, que es la del servidor, y
       * la versión anterior de esta línea la prefería: `c.clave ||
       * claveDeNombre(c.nombre)`. Suena a red de seguridad y es lo
       * contrario. La búsqueda de abajo (`tinteDe`) pregunta por
       * `claveDeNombre(nombre)`: si la clave guardada y la calculada
       * alguna vez difirieran, guardar la del servidor haría que el
       * color NO SE ENCUENTRE NUNCA. La red de seguridad rompía
       * exactamente el caso que decía cubrir.
       *
       * Que las dos coincidan no se garantiza acá: lo garantiza
       * `test_normalizacion.js` §1, que ejecuta las dos y las compara. */
      .map((c) => [claveDeNombre(c.nombre), c.hex])
  );
}

function olvidarPropios() {
  PROPIOS = new Map();
}

/**
 * El color de pantalla de un nombre, o `null` si no se conoce.
 *
 * ⚠️  DOS PREGUNTAS DISTINTAS, DOS REGLAS (BA-9, 2026-09-06)
 *
 * Durante meses esto usó UNA sola normalización —la agresiva, la que
 * saca todos los espacios— para las dos cosas, y tres comentarios
 * afirmaban que era "la misma que usa el servidor". No lo era: el
 * servidor solo hace `trim()`.
 *
 *   1. "¿Este nombre es alguno de los 21 de la lista base?"
 *      La lista base está escrita PEGADA a propósito —"Azulclaro",
 *      "Azuloscuro", "MarrónOscuro"— así que hay que sacar los espacios
 *      para que "Azul claro", como lo escribe cualquiera, la encuentre.
 *      Esa es `clavePegada`, y NO sale de este archivo.
 *
 *   2. "¿Este color propio es el mismo que aquel otro?"
 *      Esa la contesta el SERVIDOR, y la respuesta ya está guardada en
 *      `colores_usuario.clave`. Acá se usa `claveDeNombre`, que copia su
 *      regla exacta. Con la regla agresiva —como estaba—, un usuario con
 *      "Azul Claro" y "AzulClaro" (dos colores distintos para el
 *      servidor, que deja crear los dos) veía UNO SOLO en el editor de
 *      prenda, y el otro quedaba inseleccionable.
 *
 * La lista base gana sobre los propios. El servidor ya impide crear un
 * color con el nombre de uno base, así que no debería haber choque;
 * pero si esa defensa fallara, los 21 de siempre tienen que seguir
 * viéndose igual para todos.
 */
function tinteDe(nombre) {
  if (!nombre || typeof nombre !== "string") return null;
  const directo = TINTES[nombre];
  if (directo) return directo;

  const pegada = clavePegada(nombre);
  for (const [n, hex] of Object.entries(TINTES)) {
    if (clavePegada(n) === pegada) return hex;
  }
  return PROPIOS.get(claveDeNombre(nombre)) || null;
}

/* Pregunta 1. NO se exporta a propósito: usarla para comparar dos
 * colores del usuario es exactamente el bug que esto arregla. */
function clavePegada(texto) {
  return claveDeNombre(texto).replace(/\s+/g, "");
}

/**
 * Pregunta 2: ¿el servidor diría que estos dos nombres son el mismo?
 *
 * ⚠️  ESTO ES UNA COPIA DE `normalizar()` DE lib/opciones.js.
 *
 * No se puede importar: aquel corre en Node y este en el navegador, y
 * el bundle del front no arrastra `lib/`. El guardián de
 * `test_colores.js` §7 ejecuta las DOS implementaciones sobre la misma
 * batería de nombres y compara los resultados uno por uno; el día que
 * alguien cambie una y no la otra, se pone rojo. Es la única forma de
 * que una copia siga siendo una copia.
 */
function claveDeNombre(texto) {
  return String(texto || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

/**
 * Los colores de un outfit, listos para dibujar.
 *
 * Devuelve una lista de `{ nombre, tinte }` donde `tinte` es un color
 * CSS o la marca DESCONOCIDO.
 *
 * DOS DECISIONES QUE SE VEN EN PANTALLA
 *
 * 1. Se DEDUPLICA. Un outfit de camisa azul, jean azul y zapatillas
 *    azules es un día azul, no tres franjas azules. Sin esto, un día
 *    monocromático se vería igual de "variado" que uno de tres colores.
 *
 * 2. Se corta en 4. Una prenda puede tener varios colores y un outfit
 *    llega a juntar ocho; ocho franjas en una celda de calendario son
 *    ocho líneas de un píxel. Cuatro se distinguen.
 *
 * El orden es el de las prendas, que es el orden en que se registró el
 * outfit. No se ordena por nada: cualquier orden que eligiéramos
 * inventaría una jerarquía que no existe.
 */
function tintesDelOutfit(prendas, tope = 4) {
  const vistos = new Set();
  const salida = [];

  for (const p of prendas || []) {
    for (const nombre of p?.colores || []) {
      const clave = claveDeNombre(nombre);
      if (!clave || vistos.has(clave)) continue;
      vistos.add(clave);
      salida.push({ nombre, tinte: tinteDe(nombre) || DESCONOCIDO });
      if (salida.length >= tope) return salida;
    }
  }

  return salida;
}

module.exports = {
  TINTES,
  DESCONOCIDO,
  tinteDe,
  tintesDelOutfit,
  claveDeNombre,
  registrarPropios,
  olvidarPropios,
};
